import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import './ToastNotification.css';

// Toast notification context for global usage
export const ToastContext = React.createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showToast = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const newToast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    setTimeout(() => {
      removeToast(id);
    }, duration);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="toast-container">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} removeToast={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

const Toast = ({ toast, removeToast }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100 / (toast.duration / 100);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <XCircle size={20} />;
      case 'warning':
        return <AlertCircle size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  const getStyle = () => {
    switch (toast.type) {
      case 'success':
        return { bg: '#4caf50', icon: '#e8f5e9' };
      case 'error':
        return { bg: '#f44336', icon: '#ffebee' };
      case 'warning':
        return { bg: '#ff9800', icon: '#fff3e0' };
      default:
        return { bg: '#2196f3', icon: '#e3f2fd' };
    }
  };

  const style = getStyle();

  return (
    <motion.div
      className={`toast toast-${toast.type}`}
      initial={{ opacity: 0, x: 100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
      style={{ '--toast-bg': style.bg }}
    >
      <div className="toast-icon" style={{ backgroundColor: style.icon, color: style.bg }}>
        {getIcon()}
      </div>
      <p className="toast-message">{toast.message}</p>
      <button className="toast-close" onClick={() => removeToast(toast.id)}>
        <X size={16} />
      </button>
      <div
        className="toast-progress"
        style={{ width: `${progress}%`, backgroundColor: style.bg }}
      />
    </motion.div>
  );
};

// Hook to use toast notifications
export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
